import React from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { Input } from 'reactstrap';
import type { RolloutDayKey } from 'fym-shared/src/music24/releaseRollout';
import { TypedMemo } from '../../../../../common/types';
import { platformName, platformTakesMedia, supportsStories } from '../../../platformMeta';
import { isSocialPlatform, SocialPlatform } from '../../../types';
import { ChannelMark } from './ChannelMark';
import { ChannelPill } from './ChannelPill';
import { useRollout } from './RolloutContext';
import type { PlanStepView } from './rolloutModel';
import { sheetMessages } from './sheetMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the step sheet; each part uses its own slice of it */
import sheetStyles from './StepSheet.module.scss';
/* eslint-enable css-modules/no-unused-class */

/**
 * "Channels of this step" (design rollout-plan c1, owner decision 6): one row per usable channel.
 * A tick stores the channel on the step and adds its draft; an untick takes the channel off and its
 * open post leaves the plan. A posted channel stays. A channel that cannot take the step is grey
 * with its reason (the Composer rule).
 */
const StepSheetChannelsComponent: React.FC<{ step: PlanStepView }> = ({ step }) => {
  const intl = useIntl();
  const { channels, actions, canAct } = useRollout();
  const media = step.day.format === 'reel' ? 'video' : 'image';
  const ticked = step.channels.map((channel) => channel.platform).filter(isSocialPlatform);

  const reasonOf = (platform: SocialPlatform): string | null => {
    if (step.day.format === 'story' && !supportsStories(platform)) {
      return intl.formatMessage(sheetMessages.noStory);
    }
    return platformTakesMedia(platform, media) ? null : intl.formatMessage(sheetMessages.cannotTake, { kind: media });
  };

  const toggle = (platform: SocialPlatform) => {
    const next = ticked.includes(platform) ? ticked.filter((one) => one !== platform) : [...ticked, platform];
    actions.setChannelsOfStep('sheet', step.key as RolloutDayKey, next).catch(console.error);
  };

  return (
    <div className={sheetStyles.field}>
      <span className={sheetStyles.label}>{intl.formatMessage(sheetMessages.channelsTitle)}</span>
      <div className={sheetStyles.checks}>
        {channels.map(({ platform }) => {
          const reason = reasonOf(platform);
          const view = step.channels.find((channel) => channel.platform === platform);
          const isPosted = view?.state === 'posted' || view?.state === 'publishing';
          const isOn = ticked.includes(platform);
          return (
            <div key={platform} className={clsx(sheetStyles.check, reason !== null && sheetStyles.checkOff)}>
              <Input
                type="checkbox"
                checked={isOn}
                aria-label={platformName(platform)}
                disabled={!canAct || reason !== null || isPosted || actions.busy !== null}
                onChange={() => toggle(platform)}
              />
              <ChannelMark platform={platform} small />
              <span>
                <b>{platformName(platform)}</b>
                {reason !== null && ` · ${reason}`}
              </span>
              {view !== undefined ? <ChannelPill channel={view} /> : <span />}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const StepSheetChannels = TypedMemo(StepSheetChannelsComponent);
